import { useEffect, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import {
  Group,
  FileButton,
  Button,
  LoadingOverlay,
  Flex,
  Text,
  Tooltip,
  Image,
  Avatar,
} from "@mantine/core";
import { IconPhotoCancel, IconTrash } from "@tabler/icons-react";

import { useFileUpload } from "../services/react-query";
import { cropImage } from "./ImageCropModal";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";

const toDataUrl = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });

const ImageUpload = ({
  control,
  name,
  label,
  description,
  defaultImage,
  resize = false,
  aspectRatio = 1,
  removable = false,
  onPreview,
  ...props
}) => {
  const [preview, setPreview] = useState(defaultImage);

  const { isPending, mutateAsync } = useFileUpload();

  const value = useWatch({ control, name });

  // Revoke the object URL when the component unmounts
  // to avoid memory leaks
  useEffect(() => {
    return () => URL.revokeObjectURL(preview);
  }, [preview]);

  // Update preview when defaultImage changes (after reset)
  useEffect(() => {
    setPreview(defaultImage || null);
  }, [defaultImage]);

  // Keeps local preview in sync with React Hook Form value (clears preview when field becomes null)
  useEffect(() => {
    if (!value) setPreview(null);
  }, [value]);

  const handleImageUpload = async (file, onChange) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      notifications.show({
        title: "Invalid file type",
        message: "Please upload a valid image file.",
        color: "red",
      });
      return;
    }

    if (resize) {
      const croppedImage = await cropImage(file, aspectRatio);
      if (!croppedImage) return;
      file = croppedImage;
    }

    const previewUrl = URL.createObjectURL(file);
    setPreview(previewUrl);
    // The uploaded key only resolves to an image once the event is saved, and
    // an object URL belongs to this origin alone — so previews get it inline.
    onPreview?.(await toDataUrl(file));

    try {
      const data = await mutateAsync(file);
      onChange(data.fileKey);
    } catch (error) {
      notifications.show({
        title: "Upload failed",
        message:
          "An error occurred while uploading the image. Please try again.",
        color: "red",
      });
      setPreview(defaultImage);
      onPreview?.(null);
    }
  };

  const handleRemoveImage = (onChange) => {
    setPreview(null);
    onPreview?.(null);
    onChange(null);
  };

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange } }) => (
        <Flex direction="column" {...props}>
          {label && (
            <Text size="sm" fw={600} mb={description ? 0 : "sm"}>
              {label}
            </Text>
          )}
          {description && (
            <Text size="xs" c="dimmed" style={{ marginBottom: 10 }}>
              {description}
            </Text>
          )}
          <Group align={preview && removable ? "flex-start" : "center"}>
            <Flex pos="relative">
              <LoadingOverlay visible={isPending} />
              {preview ? (
                <Tooltip label="Click to see preview" withArrow>
                  <Image
                    src={preview}
                    alt="Uploaded image preview"
                    style={{ height: "75px", cursor: "pointer" }}
                    onClick={() => {
                      modals.open({
                        title: "Image preview",
                        size: "lg",
                        children: (
                          <Flex>
                            <Image src={preview} alt="Uploaded image preview" />
                          </Flex>
                        ),
                      });
                    }}
                  />
                </Tooltip>
              ) : (
                <Tooltip label="No image uploaded" withArrow>
                  <Avatar size="75px" radius="md">
                    <IconPhotoCancel size="50" />
                  </Avatar>
                </Tooltip>
              )}
            </Flex>

            <Flex direction="column" gap="xs">
              <FileButton
                onChange={(payload) => handleImageUpload(payload, onChange)}
                accept="image/png,image/jpeg,image/webp"
              >
                {(props) => (
                  <Button
                    size="compact-md"
                    variant="light"
                    loading={isPending}
                    {...props}
                  >
                    Upload picture
                  </Button>
                )}
              </FileButton>

              {preview && removable && (
                <Tooltip label="Remove image" withArrow>
                  <Button
                    size="compact-md"
                    variant="subtle"
                    color="red"
                    onClick={() => handleRemoveImage(onChange)}
                  >
                    <IconTrash size={16} style={{ marginRight: 5 }} /> Remove
                  </Button>
                </Tooltip>
              )}
            </Flex>
          </Group>
        </Flex>
      )}
    />
  );
};

export default ImageUpload;
